import React, { useState, useEffect } from 'react';
import './OrderForm.css';

function OrderForm({ 
  pair, 
  currentPrice, 
  wallet, 
  onPlaceOrder, 
  isMobile = false,
  compact = false,
  socket = null,
  hasOpenOrder = false,
  openOrderInfo = null
}) {
  const [orderType, setOrderType] = useState('buy');
  const [price, setPrice] = useState('');
  const [amount, setAmount] = useState('');
  const [total, setTotal] = useState('');
  const [orderMethod, setOrderMethod] = useState('limit');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [realTimePrice, setRealTimePrice] = useState(currentPrice);
  const [walletFreezeStatus, setWalletFreezeStatus] = useState({});

  const baseCurrency = pair?.split('/')[0] || '';
  const quoteCurrency = pair?.split('/')[1] || '';

  // Update realTimePrice when currentPrice prop changes
  useEffect(() => {
    if (currentPrice && currentPrice > 0) {
      setRealTimePrice(currentPrice);
    }
  }, [currentPrice]);

  // Update wallet freeze status
  useEffect(() => {
    const freezeStatus = {};
    
    if (Array.isArray(wallet)) {
      wallet.forEach(item => {
        freezeStatus[item.currency] = {
          is_frozen: item.is_frozen || false,
          frozen_reason: item.frozen_reason || '',
          frozen_at: item.frozen_at || null
        };
      });
    }
    
    setWalletFreezeStatus(freezeStatus);
  }, [wallet]);

  // Check if wallet is frozen for the current trade
  const checkWalletFrozen = () => {
    if (orderType === 'buy') {
      const quoteWallet = walletFreezeStatus[quoteCurrency];
      if (quoteWallet?.is_frozen) {
        return {
          isFrozen: true,
          currency: quoteCurrency,
          message: `Your ${quoteCurrency} wallet is frozen. You cannot place buy orders. Please contact support.`,
          reason: quoteWallet.frozen_reason,
          frozenAt: quoteWallet.frozen_at
        };
      }
    } else {
      const baseWallet = walletFreezeStatus[baseCurrency];
      if (baseWallet?.is_frozen) {
        return {
          isFrozen: true,
          currency: baseCurrency,
          message: `Your ${baseCurrency} wallet is frozen. You cannot place sell orders. Please contact support.`,
          reason: baseWallet.frozen_reason,
          frozenAt: baseWallet.frozen_at
        };
      }
    }
    return { isFrozen: false };
  };

  // Listen for real-time price updates from WebSocket
  useEffect(() => {
    if (!socket) return;

    const handleRealPriceUpdate = (data) => {
      if (data.pair === pair) {
        setRealTimePrice(data.price);
      }
    };

    const handleMarketData = (data) => {
      if (data[pair]) {
        setRealTimePrice(data[pair].current_price);
      }
    };

    socket.on('realPriceUpdate', handleRealPriceUpdate);
    socket.on('marketData', handleMarketData);

    return () => {
      socket.off('realPriceUpdate', handleRealPriceUpdate);
      socket.off('marketData', handleMarketData);
    };
  }, [socket, pair]);

  const walletObj = React.useMemo(() => {
    if (Array.isArray(wallet)) {
      return wallet.reduce((acc, walletItem) => {
        acc[walletItem.currency] = {
          balance: walletItem.balance,
          locked: walletItem.locked || 0,
          is_frozen: walletItem.is_frozen || false,
          frozen_reason: walletItem.frozen_reason || '',
          frozen_at: walletItem.frozen_at || null
        };
        return acc;
      }, {});
    }
    return wallet || {};
  }, [wallet]);

  // Real-time validation for sell orders - ENHANCED VALIDATION
  useEffect(() => {
    const validateSellPrice = () => {
      // First check if wallet is frozen
      const freezeCheck = checkWalletFrozen();
      if (freezeCheck.isFrozen) {
        setError(`❌ ${freezeCheck.message}`);
        return;
      }
      setError('');
    };

    validateSellPrice();
  }, [orderType, orderMethod, price]);

  // Auto-fill market price with real-time data
  useEffect(() => {
    if (orderMethod === 'market' && realTimePrice) {
      setPrice(realTimePrice.toFixed(6));
    }
  }, [orderMethod, realTimePrice]);

  // Calculate total
  useEffect(() => {
    if (price && amount) {
      const calculatedTotal = parseFloat(price) * parseFloat(amount);
      setTotal(calculatedTotal.toFixed(6));
    } else {
      setTotal('');
    }
  }, [price, amount]);

  // Request real-time price when component mounts
  useEffect(() => {
    if (socket && pair) {
      socket.emit('getRealPrice', pair);
    }
  }, [socket, pair]);

  const handlePercentageClick = (percentage) => {
    // Check if wallet is frozen first
    const freezeCheck = checkWalletFrozen();
    if (freezeCheck.isFrozen) {
      setError(`❌ ${freezeCheck.message}`);
      return;
    }

    const currentPriceValue = parseFloat(price || realTimePrice);
    
    if (orderType === 'buy') {
      const maxSpend = parseFloat(walletObj[quoteCurrency]?.balance || 0);
      const maxAmount = maxSpend / currentPriceValue;
      setAmount((maxAmount * percentage).toFixed(8));
    } else {
      const maxAmount = parseFloat(walletObj[baseCurrency]?.balance || 0);
      setAmount((maxAmount * percentage).toFixed(8));
    }
  };

  // WebSocket event handlers for order responses
  useEffect(() => {
    if (!socket) return;

    const handleOrderPlaced = (data) => {
      setIsSubmitting(false);
      if (data.success) {
        // Reset form
        setAmount('');
        setTotal('');
        if (orderMethod === 'limit') {
          setPrice('');
        }
      }
    };

    const handleOrderRejected = (data) => {
      setIsSubmitting(false);
      // Check if it's a profit margin error or frozen wallet error
      if (data.error && data.error.includes('PROFIT_MARGIN_EXCEEDED')) {
        setError(`❌ Order rejected: Profit limit exceeded!`);
      } else if (data.error && data.error.includes('WALLET_FROZEN')) {
        setError(`❌ ${data.error}`);
      } else {
        setError(data.error);
      }
    };

    socket.on('orderPlaced', handleOrderPlaced);
    socket.on('orderRejected', handleOrderRejected);

    return () => {
      socket.off('orderPlaced', handleOrderPlaced);
      socket.off('orderRejected', handleOrderRejected);
    };
  }, [socket, orderMethod]);

  // ENHANCED SUBMIT LOGIC WITH BETTER VALIDATION
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Check if wallet is frozen first
      const freezeCheck = checkWalletFrozen();
      if (freezeCheck.isFrozen) {
        throw new Error(`❌ ${freezeCheck.message}`);
      }

      // Check if there's already an open order
      if (hasOpenOrder) {
        throw new Error('❌ You already have an open order for this trading pair. Please cancel the existing order or wait for it to be filled before placing a new one.');
      }

      // Basic validation
      if (!amount || amount <= 0) {
        throw new Error('Please enter a valid amount');
      }

      if (orderMethod === 'limit' && (!price || price <= 0)) {
        throw new Error('Please enter a valid price for limit order');
      }

      // Get user info
      const user = JSON.parse(localStorage.getItem('user'));
      const token = localStorage.getItem('token');
      
      if (!user || !token) {
        throw new Error('Please login to place orders');
      }

      // Use real-time price for calculations
      const finalPrice = orderMethod === 'market' ? realTimePrice : parseFloat(price);
      const finalTotal = finalPrice * parseFloat(amount);

      // Check balance
      if (orderType === 'buy') {
        const availableBalance = parseFloat(walletObj[quoteCurrency]?.balance || 0);
        if (finalTotal > availableBalance) {
          throw new Error(`Insufficient ${quoteCurrency} balance. Available: ${availableBalance}`);
        }
      } else {
        const availableBalance = parseFloat(walletObj[baseCurrency]?.balance || 0);
        if (parseFloat(amount) > availableBalance) {
          throw new Error(`Insufficient ${baseCurrency} balance. Available: ${availableBalance}`);
        }
      }

      // Prepare order data
      const orderData = {
        userId: user.id,
        pair: pair,
        type: orderType,
        order_type: orderMethod,
        price: orderMethod === 'limit' ? parseFloat(price) : null,
        amount: parseFloat(amount),
        total: finalTotal
      };

      console.log('📤 Placing order with validation:', orderData);

      // Call the parent onPlaceOrder function
      await onPlaceOrder(orderData);
      
      // Reset form on success
      setAmount('');
      setTotal('');
      if (orderMethod === 'limit') {
        setPrice('');
      }
      setIsSubmitting(false);

    } catch (error) {
      setIsSubmitting(false);
      setError(error.message);
      console.error('Order submission error:', error);
    }
  };

  const availableBalance = orderType === 'buy' 
    ? parseFloat(walletObj[quoteCurrency]?.balance || 0).toFixed(2)
    : parseFloat(walletObj[baseCurrency]?.balance || 0).toFixed(6);

  const lockedBalance = orderType === 'buy'
    ? parseFloat(walletObj[quoteCurrency]?.locked || 0).toFixed(2)
    : parseFloat(walletObj[baseCurrency]?.locked || 0).toFixed(6);

  // Check if user can sell this currency
  const canSellCurrency = walletObj[baseCurrency] && parseFloat(walletObj[baseCurrency].balance) > 0;

  // Check if wallet is frozen for display
  const freezeCheck = checkWalletFrozen();
  const isWalletFrozen = freezeCheck.isFrozen;

  // Mobile layout
  if (isMobile) {
    return (
      <div className="order-form-mobile compact">
        {/* Toggle Buy/Sell */}
        <div className="mobile-type-toggle">
          <button 
            className={`type-btn buy ${orderType === 'buy' ? 'active' : ''}`}
            onClick={() => setOrderType('buy')}
          >
            Buy
          </button>
          <button 
            className={`type-btn sell ${orderType === 'sell' ? 'active' : ''}`}
            onClick={() => setOrderType('sell')}
          >
            Sell
          </button>
        </div>

        {/* Limit/Market Select */}
        <div className="mobile-method-select">
           <select 
             value={orderMethod} 
             onChange={(e) => setOrderMethod(e.target.value)}
             className="mobile-select"
           >
             <option value="limit">Limit Order</option>
             <option value="market">Market Order</option>
           </select>
        </div>

        <form onSubmit={handleSubmit} className="mobile-form">
          {/* Price Input */}
           <div className="mobile-input-group">
             <input
               type="number"
               value={price}
               onChange={(e) => setPrice(e.target.value)}
               disabled={orderMethod === 'market'}
               placeholder={orderMethod === 'market' ? 'Market Price' : 'Price'}
               className="mobile-input"
             />
             <span className="unit">{quoteCurrency}</span>
           </div>

          {/* Amount Input */}
           <div className="mobile-input-group">
             <input
               type="number"
               value={amount}
               onChange={(e) => setAmount(e.target.value)}
               placeholder="Amount"
               className="mobile-input"
             />
             <span className="unit">{baseCurrency}</span>
           </div>

           {/* Percentages Slider/Buttons */}
           <div className="mobile-percentages">
             {[25, 50, 75, 100].map(perc => (
               <button
                 key={perc}
                 type="button"
                 className="mobile-percent-btn"
                 onClick={() => handlePercentageClick(perc/100)}
               >
                 {perc}%
               </button>
             ))}
           </div>

           {/* Total (ReadOnly) */}
           <div className="mobile-total">
             <span>Total:</span>
             <span>{total || '0.00'} {quoteCurrency}</span>
           </div>

           {/* Avail Balance */}
           <div className="mobile-avail">
             Avail: {availableBalance} {orderType === 'buy' ? quoteCurrency : baseCurrency}
           </div>

           {/* Submit Button */}
           <button 
             type="submit" 
             className={`mobile-submit-btn ${orderType}`}
             disabled={isSubmitting || hasOpenOrder || isWalletFrozen}
           >
             {orderType === 'buy' ? 'Buy' : 'Sell'} {baseCurrency}
           </button>
        </form>
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="order-form">
      {/* Header with Gradient */}
      <div className="order-header">
        <div className="header-gradient">
          <h3>📊 Trade {pair}</h3>
          <div className="current-price">${realTimePrice?.toFixed(2) || '0.00'}</div>
        </div>
      </div>

      {/* Wallet Frozen Warning */}
      {isWalletFrozen && (
        <div className="frozen-wallet-alert">
          <div className="alert-content">
            <div className="alert-icon">❄️</div>
            <div className="alert-text">
              <span>Wallet Frozen - Trading Disabled</span>
              <small>{freezeCheck.message}</small>
              {freezeCheck.reason && (
                <div className="freeze-reason">
                  <strong>Reason:</strong> {freezeCheck.reason}
                </div>
              )}
              {freezeCheck.frozenAt && (
                <div className="freeze-date">
                  <strong>Frozen since:</strong> {new Date(freezeCheck.frozenAt).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Open Order Warning */}
      {hasOpenOrder && (
        <div className="open-order-alert">
          <div className="alert-content">
            <div className="alert-icon">⚠️</div>
            <div className="alert-text">
              <span>You have an open order for {pair}</span>
              <small>Please cancel the existing order or wait for it to be filled before placing a new one.</small>
            </div>
          </div>
        </div>
      )}

      {/* Order Type Toggle */}
      <div className="modern-toggle">
        <button 
          className={`toggle-option ${orderType === 'buy' ? 'active buy' : ''}`}
          onClick={() => setOrderType('buy')}
          disabled={hasOpenOrder || isWalletFrozen}
        >
          <span className="icon">📈</span>
          <span className="text">Buy</span>
          {isWalletFrozen && orderType === 'buy' && <span className="freeze-indicator">❄️</span>}
        </button>
        <button 
          className={`toggle-option ${orderType === 'sell' ? 'active sell' : ''}`}
          onClick={() => setOrderType('sell')}
          disabled={!canSellCurrency || hasOpenOrder || isWalletFrozen}
        >
          <span className="icon">📉</span>
          <span className="text">Sell</span>
          {!canSellCurrency && <span className="no-holdings-tooltip">No {baseCurrency}</span>}
          {isWalletFrozen && orderType === 'sell' && <span className="freeze-indicator">❄️</span>}
        </button>
      </div>

      {/* Order Method Toggle */}
      <div className="method-toggle">
        <button 
          className={orderMethod === 'limit' ? 'active' : ''}
          onClick={() => setOrderMethod('limit')}
          disabled={hasOpenOrder || isWalletFrozen}
        >
          ⚡ Limit
        </button>
        <button 
          className={orderMethod === 'market' ? 'active' : ''}
          onClick={() => setOrderMethod('market')}
          disabled={hasOpenOrder || isWalletFrozen}
        >
          🎯 Market
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Price Input with Profit Indicator */}
        <div className="input-group">
          <label htmlFor="price">
            💰 Price ({quoteCurrency})
          </label>
          <div className="input-with-status">
            <input
              type="number"
              id="price"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              disabled={orderMethod === 'market' || hasOpenOrder || isWalletFrozen}
              step="0.000001"
              placeholder={orderMethod === 'market' ? 'Market Price' : 'Enter price...'}
              className={error ? 'error' : ''}
            />
          </div>
          {error && <div className="error-message">{error}</div>}
        </div>

        {/* Amount Input */}
        <div className="input-group">
          <label htmlFor="amount">🔢 Amount ({baseCurrency})</label>
          <input
            type="number"
            id="amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            step="0.000001"
            placeholder="Enter amount..."
            disabled={hasOpenOrder || isWalletFrozen}
          />
          <div className="quick-percentages">
            {[25, 50, 75, 100].map(perc => (
              <button
                key={perc}
                type="button"
                className="percent-btn"
                onClick={() => handlePercentageClick(perc/100)}
                disabled={hasOpenOrder || isWalletFrozen}
              >
                {perc}%
              </button>
            ))}
          </div>
        </div>

        {/* Total Display */}
        <div className="input-group">
          <label htmlFor="total">💵 Total ({quoteCurrency})</label>
          <input
            type="number"
            id="total"
            value={total}
            readOnly
            placeholder="0.00"
            className="total-display"
          />
        </div>

        {/* Balance Info with Freeze Status */}
        <div className={`balance-card ${isWalletFrozen ? 'frozen' : ''}`}>
          <div className="balance-header">
            <span>💼 Available Balance</span>
            <span className="balance-amount">
              {availableBalance} {orderType === 'buy' ? quoteCurrency : baseCurrency}
              {isWalletFrozen && <span className="freeze-badge">❄️ FROZEN</span>}
            </span>
          </div>
          {parseFloat(lockedBalance) > 0 && (
            <div className="locked-balance">
              <span>🔒 Locked</span>
              <span>{lockedBalance} {orderType === 'buy' ? quoteCurrency : baseCurrency}</span>
            </div>
          )}
          {isWalletFrozen && (
            <div className="freeze-info">
              <div className="freeze-warning">
                <span className="warning-icon">🚫</span>
                <span>Trading disabled for this wallet</span>
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <button 
          type="submit" 
          className={`submit-button ${orderType} ${
            error || isSubmitting || hasOpenOrder || isWalletFrozen ? 'disabled' : ''
          }`}
          disabled={
            hasOpenOrder ||
            isWalletFrozen ||
            (error && orderType === 'sell' && orderMethod === 'limit') || 
            isSubmitting || 
            (orderType === 'sell' && !canSellCurrency)
          }
        >
          {hasOpenOrder ? (
            <span className="button-text">
              <span className="button-icon">⏳</span>
              Open Order Exists
            </span>
          ) : isWalletFrozen ? (
            <span className="button-text">
              <span className="button-icon">❄️</span>
              Wallet Frozen
            </span>
          ) : isSubmitting ? (
            <div className="button-loading">
              <div className="loading-spinner"></div>
              <span>Processing...</span>
            </div>
          ) : (
            <span className="button-text">
              <span className="button-icon">
                {orderType === 'buy' ? '🛒' : '💸'}
              </span>
              {orderType === 'buy' ? `Buy ${baseCurrency}` : `Sell ${baseCurrency}`}
              <span className="order-type">{orderMethod === 'market' ? ' (Market)' : ' (Limit)'}</span>
            </span>
          )}
        </button>

        {error && (
          <div className="form-error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}
      </form>
    </div>
  );
}

export default OrderForm;